import apiClient from "../tools/axiosConfig";

const normalizeEntityId = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  if (!normalized || normalized.toLowerCase() === "new") return null;
  return normalized;
};

const lessonBundleCache = new Map();
const inflightTasks = new Map();

const runInflightTask = (key, runner) => {
  if (inflightTasks.has(key)) {
    return inflightTasks.get(key);
  }
  const task = Promise.resolve()
    .then(runner)
    .finally(() => {
      if (inflightTasks.get(key) === task) {
        inflightTasks.delete(key);
      }
    });
  inflightTasks.set(key, task);
  return task;
};

/**
 * Loads lesson metadata + all versions + steps from the network (uncached).
 * Used by {@link ensureLessonBundle}.
 */
export async function loadLessonBundle(lessonId) {
  const normalizedLessonId = normalizeEntityId(lessonId);
  if (!normalizedLessonId) return null;

  const { data: versionsData } = await apiClient.get(`/backoffice/v1/lessons/${normalizedLessonId}/versions/`);
  const versions = Array.isArray(versionsData) ? [...versionsData] : [];
  const stepResults = await Promise.all(
    versions.map(async (lessonVersion) => {
      if (!lessonVersion?.id) return [];
      const stepsRes = await apiClient.get(`/backoffice/v1/lesson-versions/${lessonVersion.id}/steps/`);
      return Array.isArray(stepsRes.data) ? stepsRes.data : [];
    })
  );

  versions.forEach((lessonVersion, index) => {
    lessonVersion.lessonSteps = Array.isArray(stepResults[index]) ? stepResults[index] : [];
  });

  const lessonRes = await apiClient.get(`/backoffice/v1/lessons/${normalizedLessonId}/`);
  return {
    lesson: {
      ...lessonRes.data,
      lessonVersions: versions,
    },
    lessonVersions: versions,
  };
}

export function readLessonBundle(lessonId) {
  const normalizedLessonId = normalizeEntityId(lessonId);
  if (!normalizedLessonId) return null;
  return lessonBundleCache.get(normalizedLessonId) || null;
}

export async function ensureLessonBundle(lessonId, { force = false } = {}) {
  const normalizedLessonId = normalizeEntityId(lessonId);
  if (!normalizedLessonId) return null;
  if (!force && lessonBundleCache.has(normalizedLessonId)) {
    return lessonBundleCache.get(normalizedLessonId);
  }
  const bundle = await runInflightTask(
    force ? `lessonBundle:${normalizedLessonId}:refresh` : `lessonBundle:${normalizedLessonId}`,
    async () => loadLessonBundle(normalizedLessonId)
  );
  lessonBundleCache.set(normalizedLessonId, bundle);
  return bundle;
}

export function patchLessonBundle(lessonId, updater) {
  const normalizedLessonId = normalizeEntityId(lessonId);
  if (!normalizedLessonId) return;
  const currentBundle = lessonBundleCache.get(normalizedLessonId) || null;
  const nextBundle = typeof updater === "function" ? updater(currentBundle) : updater;
  if (!nextBundle) return;
  lessonBundleCache.set(normalizedLessonId, nextBundle);
}
